import { useState } from 'react'
import { Plus, Edit2, Trash2, X, CheckSquare, AlertTriangle, TrendingUp } from 'lucide-react'

export default function ComplianceDashboardPage() {
  const [standards, setStandards] = useState([
    {
      id: 1,
      name: 'GDPR Compliance',
      framework: 'GDPR',
      status: 'compliant',
      coverage: 95,
      lastAudit: new Date().toLocaleDateString(),
      nextAudit: new Date(Date.now() + 7776000000).toLocaleDateString()
    },
    {
      id: 2,
      name: 'HIPAA Compliance',
      framework: 'HIPAA',
      status: 'compliant',
      coverage: 98,
      lastAudit: new Date().toLocaleDateString(),
      nextAudit: new Date(Date.now() + 7776000000).toLocaleDateString()
    },
    {
      id: 3,
      name: 'SOC 2 Type II',
      framework: 'SOC 2',
      status: 'in-progress',
      coverage: 85,
      lastAudit: new Date(Date.now() - 2592000000).toLocaleDateString(),
      nextAudit: new Date(Date.now() + 5184000000).toLocaleDateString()
    }
  ])

  const [showModal, setShowModal] = useState(false)
  const [editingStandard, setEditingStandard] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    framework: '',
    status: 'in-progress'
  })

  const handleCreateClick = () => {
    setEditingStandard(null)
    setFormData({
      name: '',
      framework: '',
      status: 'in-progress'
    })
    setShowModal(true)
  }

  const handleEditStandard = (standard) => {
    setEditingStandard(standard)
    setFormData({
      name: standard.name,
      framework: standard.framework,
      status: standard.status
    })
    setShowModal(true)
  }

  const handleSaveStandard = () => {
    if (!formData.name || !formData.framework) {
      alert('Please fill in all required fields')
      return
    }

    if (editingStandard) {
      setStandards(standards.map(s =>
        s.id === editingStandard.id
          ? { ...s, ...formData }
          : s
      ))
    } else {
      const newStandard = {
        id: Date.now(),
        ...formData,
        coverage: 0,
        lastAudit: 'Pending',
        nextAudit: new Date(Date.now() + 7776000000).toLocaleDateString()
      }
      setStandards([...standards, newStandard])
    }

    setShowModal(false)
  }

  const handleDeleteStandard = (id) => {
    if (confirm('Are you sure you want to delete this compliance record?')) {
      setStandards(standards.filter(s => s.id !== id))
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'compliant': return 'bg-green-600/20 text-green-400'
      case 'in-progress': return 'bg-yellow-600/20 text-yellow-400'
      case 'non-compliant': return 'bg-red-600/20 text-red-400'
      default: return 'bg-blue-600/20 text-blue-400'
    }
  }

  const getProgressColor = (coverage) => {
    if (coverage >= 95) return 'bg-green-600'
    if (coverage >= 80) return 'bg-yellow-600'
    return 'bg-red-600'
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <CheckSquare className="w-8 h-8 text-blue-400" />
            <h1 className="text-3xl font-bold text-white">Compliance Dashboard</h1>
          </div>
          <p className="text-slate-400">Track compliance status across multiple frameworks and standards</p>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Total Frameworks</div>
            <div className="text-3xl font-bold text-blue-400">{standards.length}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Compliant</div>
            <div className="text-3xl font-bold text-green-400">{standards.filter(s => s.status === 'compliant').length}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">In Progress</div>
            <div className="text-3xl font-bold text-yellow-400">{standards.filter(s => s.status === 'in-progress').length}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Avg Coverage</div>
            <div className="text-3xl font-bold text-cyan-400">{Math.round(standards.reduce((sum, s) => sum + s.coverage, 0) / standards.length)}%</div>
          </div>
        </div>

        {/* Create Button */}
        <button
          onClick={handleCreateClick}
          className="mb-6 flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-semibold rounded-lg transition-all"
        >
          <Plus className="w-5 h-5" />
          Add Framework
        </button>

        {/* Standards List */}
        <div className="space-y-4">
          {standards.map((standard) => (
            <div
              key={standard.id}
              className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <CheckSquare className="w-5 h-5 text-blue-400" />
                    <h3 className="text-lg font-semibold text-white">{standard.name}</h3>
                    <span className={`inline-flex items-center px-3 py-1 rounded text-xs font-bold ${getStatusColor(standard.status)}`}>
                      {standard.status.toUpperCase().replace('-', ' ')}
                    </span>
                  </div>
                  <p className="text-slate-400 mb-4">{standard.framework} Framework</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEditStandard(standard)}
                    className="p-2 bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 rounded-lg transition-all"
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDeleteStandard(standard.id)}
                    className="p-2 bg-red-600/20 text-red-400 hover:bg-red-600/30 rounded-lg transition-all"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Coverage Progress Bar */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-semibold text-slate-300">Compliance Coverage</span>
                  <span className="text-sm font-bold text-white">{standard.coverage}%</span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${getProgressColor(standard.coverage)}`}
                    style={{ width: `${standard.coverage}%` }}
                  ></div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-slate-700/30 rounded-lg p-3">
                  <span className="text-slate-500">Last Audit</span>
                  <div className="text-white font-semibold">{standard.lastAudit}</div>
                </div>
                <div className="bg-slate-700/30 rounded-lg p-3">
                  <span className="text-slate-500">Next Audit</span>
                  <div className="text-white font-semibold">{standard.nextAudit}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-lg border border-blue-500/20 p-8 max-w-2xl w-full mx-4">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">
                  {editingStandard ? 'Edit Compliance Framework' : 'Add Compliance Framework'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-1 hover:bg-slate-700 rounded-lg transition-all"
                >
                  <X className="w-6 h-6 text-slate-400" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Framework Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
                    placeholder="e.g., GDPR Compliance"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Standard Type *</label>
                  <select
                    value={formData.framework}
                    onChange={(e) => setFormData({ ...formData, framework: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-blue-500 focus:outline-none"
                  >
                    <option value="">Select Framework</option>
                    <option value="GDPR">GDPR</option>
                    <option value="HIPAA">HIPAA</option>
                    <option value="SOC 2">SOC 2</option>
                    <option value="ISO 27001">ISO 27001</option>
                    <option value="PCI DSS">PCI DSS</option>
                    <option value="CCPA">CCPA</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Compliance Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-blue-500 focus:outline-none"
                  >
                    <option value="in-progress">In Progress</option>
                    <option value="compliant">Compliant</option>
                    <option value="non-compliant">Non-Compliant</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-semibold transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveStandard}
                  className="px-6 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white rounded-lg font-semibold transition-all"
                >
                  Save Framework
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
